import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { getPatientRecords, type ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { useFamily } from "./FamilyContext";

// Fetch-once-per-session, same shape as MedicationsContext/DiagnosticReportsContext/
// VisitsContext — but this one is deliberately unfiltered (every resource type,
// defaulting to the patient's latest completed upload per the backend's own
// default). Backs the Dashboard's Total/Normal/Needs Attention/This Month stat
// cards. Named ParsedRecordsContext (not "records") specifically to avoid
// confusion with the older, separate PatientDataContext.records (HealthRecord[],
// only ever populated by the legacy local-file-parse path, never by a real
// zip upload) — the two are unrelated and this one is the real one.
interface ParsedRecordsContextValue {
  records: ParsedRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const ParsedRecordsContext = createContext<ParsedRecordsContextValue | null>(null);

export function ParsedRecordsProvider({ children }: { children: ReactNode }) {
  // Re-fetches whenever the patient switches which profile they're viewing
  // (see FamilyContext.activeDependent) — Medications/DiagnosticReports/Visits
  // all derive from this one fetch, so scoping it here scopes all three.
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;

  const [records, setRecords] = useState<ParsedRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setRecords([]); // clear the previous profile's records immediately — never show them while the new ones load
    getPatientRecords({ actingAsProfileId })
      .then((result) => {
        if (cancelled) return;
        setRecords(result);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load your records. Please try again later.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken, actingAsProfileId]);

  return (
    <ParsedRecordsContext.Provider value={{ records, isLoading, error, refresh }}>
      {children}
    </ParsedRecordsContext.Provider>
  );
}

export function useParsedRecords() {
  const ctx = useContext(ParsedRecordsContext);
  if (!ctx) throw new Error("useParsedRecords must be used within ParsedRecordsProvider");
  return ctx;
}
